import React from 'react';
import classnames from 'classnames';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { withTranslation } from '@wix/yoshi-flow-editor';
import { getSubscriptionPriceInfo } from '@wix/wix-vod-shared/common';
import Icon from '../icon/icon';
import { LinkButton } from '../buttons/buttons';
import Button from '../button/button';
import { getPaymentPermissions } from '../../constants/payments';
import { PaymentType } from '@wix/wix-vod-constants/api-types';
import { paymentPermissionTranslations } from '../../constants/translations';
import { MessageWithPrice } from '../message-with-price/message-with-price';
import { DateTimeFormat } from '../date-time-format/date-time-format';

export const ChannelSubscriptionBase = withTranslation()(
  class extends React.Component {
    static propTypes = {
      onSubscribeClick: PropTypes.func,
      onSubscriptionCancelClick: PropTypes.func,
      channelData: PropTypes.object.isRequired,
      className: PropTypes.string,
      downloadEnabled: PropTypes.bool.isRequired,
      styles: PropTypes.object.isRequired,
    };

    handleSubscribeClick = (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.props.onSubscribeClick();
    };

    getContent() {
      const {
        onSubscriptionCancelClick,
        channelData: { dgsInfo, dealInfo },
        styles,
        t,
        i18n,
      } = this.props;
      const subscriptionDealInfo = getSubscriptionPriceInfo(dealInfo);

      if (!subscriptionDealInfo) {
        return null;
      }

      if (dgsInfo && dgsInfo.subscription) {
        const { dateTs, price, currency, expirationTs } = dgsInfo.subscription;

        if (expirationTs) {
          return (
            <>
              <p>{t('widget.payments.subscription-canceled')}</p>
              <p>{t('widget.payments.subscription-expiration')}</p>
              <p>
                <DateTimeFormat
                  date={new Date(expirationTs * 1000)}
                  options={{
                    year: 'numeric',
                    month: 'long',
                    day: '2-digit',
                  }}
                />
              </p>
            </>
          );
        }

        const values = {
          date: new Intl.DateTimeFormat(i18n.language, {
            year: 'numeric',
            month: 'long',
            day: '2-digit',
          }).format(new Date(dateTs * 1000)),
          price: new Intl.NumberFormat(i18n.language, {
            style: 'currency',
            currency,
          }).format(price),
        };

        return (
          <div>
            <div className={styles['subscription-price']}>
              {t('widget.payments.subscribed-since', values)}
            </div>

            <LinkButton
              className={styles['cancel-button']}
              dataHook="cancel-subscription-button"
              onClick={onSubscriptionCancelClick}
            >
              {this.props.t('widget.payments.cancel-subscription')}
            </LinkButton>
          </div>
        );
      }

      return (
        <div>
          <div className={styles['subscription-price']}>
            <MessageWithPrice
              messageId="widget.payments.subscription-month"
              price={subscriptionDealInfo.price}
              currency={subscriptionDealInfo.currency}
            />
          </div>

          <div data-hook="subscriptionPerms">{this.getSubscriptionPerms()}</div>

          <Button
            className={classnames(
              styles['subscribe-button'],
              styles['with-mobile-colors'],
            )}
            dataHook="subscribe-button"
            onClick={this.handleSubscribeClick}
          >
            {this.props.t('widget.payments.subscribe-to-channel')}
          </Button>
        </div>
      );
    }

    getSubscriptionPerms() {
      const { styles } = this.props;
      const dealInfo = getSubscriptionPriceInfo(
        this.props.channelData.dealInfo,
      );

      if (!dealInfo) {
        return null;
      }

      const permissions = getPaymentPermissions({
        paymentType: PaymentType.SUBSCRIPTION,
        downloadEnabled: this.props.downloadEnabled,
      });

      return _.map(permissions, (permTitle) => (
        <div
          className={styles.permission}
          key={permTitle}
          data-hook={permTitle}
        >
          <Icon className={styles.icon} name="arrow-right-filled" />
          {this.props.t(paymentPermissionTranslations[permTitle], dealInfo)}
        </div>
      ));
    }

    render() {
      const {
        className,
        channelData: { title },
        styles,
      } = this.props;

      return (
        <div className={className}>
          <div className={styles['popout-title']}>{title}</div>

          {this.getContent()}
        </div>
      );
    }
  },
);
